import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo, startTransition } from "react";
import {
    Box,
    Typography,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    ListItemIcon,
    Fab,
    Checkbox,
    Popper,
    Paper,
    Fade,
    Slide,
    CircularProgress,
} from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
 
import "./TransparentDataGrid.css";
import Dots from "./resources/three_white_dots_vertical.svg";
import { createPortal } from "react-dom";
import CadmusLight from "./resources/cadmus_light.svg";
import portals from "./components/portals.js";
import ButtonGlass from "./components/ButtonGlass.js";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useDebounce } from "use-debounce";
import { handleDownloadFile } from "./utils/gsc.js";
import PdfOrderProcessor from "./utils/pdfOrderProcessor.js";
import { downloadAndZipFiles, getPatientInitials } from "./utils/FileDownloader.js";
import { getColumns } from "./DatagridColumns.js"
import apiClient from "./utils/apiClient.js";
import { LicenseInfo } from '@mui/x-license';
import consoleIcon from "./resources/icons/console.svg";
import PdfIcon from "./resources/icons/PdfIcon.svg";
import CadIconBlack from "./resources/icons/three_d_black.svg"
import PlyIcon from "./resources/icons/3d-with-color-spark.svg"
import PlyIconBlack from "./resources/icons/3d-with-color-spark-black.svg"
import cad from "./resources/icons/cad.svg";
import DetailsIcon from "./resources/icons/DetailsIcon.svg";
import OrderformIcon from "./resources/icons/Orderform.svg";
import ConsoleDialog from "./components/ConsoleDialog.js";
import UnavailableIcon from "./resources/icons/UnavailableIcon.svg";
import { FEATURE_ACCEPT_ENABLED, FEATURE_FILTER_MODE_ENABLED, ROW_GLOW_BLOCKS } from "./featureFlags";
import { useMobile } from "./hooks/useMobile";
import useRowGlowBlocks from "./hooks/useRowGlowBlocks";
import FlagFilledIcon from "./resources/icons/flag-filled.svg";
import { getRandomDentist, getRandomClinic, getRandomPatient } from "./components/demoData";
import CaseDetailDrawer from './CaseDetailDrawer';
import { CASE_PREFETCH } from './utils/casePrefetch';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion, AnimatePresence } from "framer-motion";


const PAGE_SIZE = 50;
// Bijladen tijdens scrollen via "rijen onder de laagste id" (keyset) i.p.v. OFFSET,
// en zonder telling per pagina. false = terug naar de oude manier.
const USE_KEYSET_PAGING = true;
// Paginagrootte bij bijladen via keyset (minder verzoeken bij hard scrollen).
// De OFFSET-manier blijft op PAGE_SIZE, want die rekent met page × PAGE_SIZE.
const MORE_PAGE_SIZE = 100;
// Bijladen starten als er nog zoveel schermhoogtes onder de huidige positie
// zitten, zodat de volgende pagina meestal al binnen is (was 0.8)
const LOAD_AHEAD_SCREENS = 3;
const SECTION_HEADERS_ENABLED = true; // set false to remove date section headers
const BUTTON_MAX_LIMITS = {
    pdf: 50,
    zip: 5,
    stl: 5
};
const handleError = (message) => {
    console.error("Error:", message);
    alert(`An error occurred: ${message}`);
};
const handleDownloadFiles = async (rowIds, fileTypeFilter, apiRef, setIsDownloading, setProgress, setStatus, setPopperOpen) => {
    // Selection order (the order rows were checked) doesn't necessarily match the row order
    // shown in the grid, so re-order the selected ids the same way the grid itself sorts rows:
    // highest id (newest) first.
    const orderedRowIds = [...rowIds].sort((a, b) => Number(b) - Number(a));
    const fileEntries = orderedRowIds.flatMap((id) => {
        const row = apiRef.current.getRow(id);
        const initials = getPatientInitials(row?.patientName);
        const ref = row?.ref || '';
        const orderNumber = row?.id || '';
        const links = [];
        try {
            const parsed = JSON.parse(row?.stlfiles);
            if (Array.isArray(parsed)) {
                const stlLinks = parsed
                    .filter((file) =>
                        typeof file.fileLink === 'string' &&
                        (fileTypeFilter ? file.fileType === fileTypeFilter : true)
                    )
                    .map((file) => file.fileLink);
                links.push(...stlLinks);
            }
        } catch (err) {
            console.error('Failed to parse stlfiles for row:', id, err);
        }
        return links.map((fileLink) => ({ fileLink, initials, ref, orderNumber }));
    });
    await downloadAndZipFiles(fileEntries, setIsDownloading, setProgress, (message) => {
        setStatus(message);
        setPopperOpen(true);
    });
};


const isButtonDisabled = (buttonType, selectedCount) => {
    return selectedCount > BUTTON_MAX_LIMITS[buttonType];
};
const ActionsMenuCell = ({ row, apiRef, setIsDownloading, setProgress, setStatus, setPopperOpen, onOpenDetails, onUpdateRow }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const menuTextStyle = {
        color: "#111",
        fontFamily: "DM Sans",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "16px",
    };
    const dividerStyle = {
        my: 1,
        mx: 1,
        borderColor: "rgba(17, 17, 17, 0.10)",
    };
    const getMenuPosition = () => {
        if (!anchorEl) return { top: 0, left: 0, isAbove: false };
        const rect = anchorEl.getBoundingClientRect();
        const menuHeight = 160;
        const spaceBelow = window.innerHeight - rect.bottom;
        const isAbove = spaceBelow < menuHeight && rect.top > menuHeight;
        return {
            top: isAbove ? rect.top - menuHeight - 2 : rect.bottom + 10,
            left: rect.right - 240 + 15,
            isAbove,
        };
    };
    const { top, left, isAbove } = getMenuPosition();
    const FileMenuItem = ({ icon, label, ext, onClick }) => (
        <MenuItem onClick={onClick}>
            <ListItemIcon sx={{ minWidth: 24 }}>{icon}</ListItemIcon>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    pb: 1,
                    pr: 1,
                }}
            >
                <Typography sx={menuTextStyle}>{label}</Typography>
                <Typography
                    sx={{
                        ...menuTextStyle,
                        color: "rgba(17, 17, 17, 0.6)",
                        fontSize: "11px",
                        fontWeight: "600",
                    }}
                >
                    {ext}
                </Typography>
            </Box>
        </MenuItem>
    );
    return (
        <>
            <IconButton
                onClick={handleClick}
                sx={{
                    display: "inline-flex",
                    height: "36px",
                    width: "24px",
                    padding: "4px 6px",
                    alignItems: "center",
                    gap: "4px",
                    flexShrink: "0",
                    borderRadius: "4px",
                    border: "1px solid transparent",
                    background: "transparent",
                    backdropFilter: "none",
                    "&:hover": {
                        border: "1px solid rgba(255, 255, 255, 0.10)",
                        background: "rgba(255, 255, 255, 0.10)",
                        backdropFilter: "blur(8px)",
                    },
                }}
            >
                <img src={Dots} alt="Menu" style={{ width: 12, height: 12 }} />
            </IconButton>
            <Menu
                anchorReference="anchorPosition"
                anchorPosition={{ top, left }}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: "240px",
                            overflow: "visible",
                            mt: 0,
                            "&:before": anchorEl
                                ? {
                                    content: '""',
                                    display: "block",
                                    position: "absolute",
                                    width: 24,
                                    height: 12,
                                    bgcolor: "background.paper",
                                    boxShadow: "0px -1px 3px rgba(0, 0, 0, 0.1)",
                                    zIndex: 0,
                                    left:
                                        anchorEl.getBoundingClientRect().left +
                                        anchorEl.offsetWidth / 2 -
                                        (anchorEl.getBoundingClientRect().right - 240 + 15) -
                                        12,
                                    ...(isAbove
                                        ? {
                                            bottom: -6,
                                            clipPath: "polygon(50% 100%, 0% 0%, 100% 0%)",
                                        }
                                        : {
                                            top: -6,
                                            clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                                        }),
                                }
                                : {},
                        },
                    },
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <MenuItem onClick={() => {
                    onOpenDetails(row);
                    handleClose();
                }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                        <img src={DetailsIcon} alt="Details" style={{ width: 20, height: 20 }} />
                    </ListItemIcon>
                    <Typography sx={menuTextStyle}>Details</Typography>
                </MenuItem>

                {FEATURE_ACCEPT_ENABLED() && row.status !== 'accepted' && <Divider sx={dividerStyle} />}
                {FEATURE_ACCEPT_ENABLED() && row.status !== 'accepted' && (
                    <MenuItem onClick={async () => {
                        handleClose();
                        apiRef.current.updateRows([{ id: row.id, flag: 1, status: 'accepted' }]);
                        onUpdateRow?.(row.id, { flag: 1, status: 'accepted' });
                        try {
                            await apiClient.patch(`/cases/${row.id}/status`, { status: 'accepted' });
                            if (row.configuredPortalId) {
                                apiClient.post('/processing_queue', { configured_portal_id: row.configuredPortalId, external_id: row.externalId, action: 'status', value: 'accepted' }).catch(console.error);
                            }
                        } catch (err) {
                            console.error("Failed to accept case:", err);
                            apiRef.current.updateRows([{ id: row.id, flag: row.flag, status: row.status }]);
                            onUpdateRow?.(row.id, { flag: row.flag, status: row.status });
                        }
                    }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                            <Box sx={{ position: 'relative', width: 20, height: 20 }}>
                                <CheckCircleIcon sx={{ fontSize: 18, width: 18, height: 18, color: '#1a1a1a' }} />
                                <Box sx={{
                                    position: 'absolute',
                                    top: -1,
                                    right: 2,
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    backgroundColor: '#1D9E75',
                                    border: '2.5px solid #fff',
                                }} />
                            </Box>
                        </ListItemIcon>
                        <Typography sx={menuTextStyle}>Accept Case</Typography>
                    </MenuItem>
                )}
                {FEATURE_ACCEPT_ENABLED() && row.status !== 'accepted' && <Divider sx={dividerStyle} />}

                <MenuItem onClick={() => {
                    handleDownloadFile(row.orderform, handleError);
                    handleClose();
                }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                        <img src={OrderformIcon} alt="Order" style={{ width: 20, height: 20 }} />
                    </ListItemIcon>
                    <Typography sx={menuTextStyle}>IOS Form</Typography>
                </MenuItem>


                <Divider sx={dividerStyle} />

                <FileMenuItem
                    icon={
                        <img
                            src={CadIconBlack}
                            alt="Cad icon"
                            style={{ width: 20, height: 20, color: "#000", verticalAlign: "middle" }}
                        />
                    }
                    label="3D Files"
                    ext=".STL"
                    onClick={() => {
                        handleDownloadFiles(
                            [row.id],
                            'stl',
                            apiRef,
                            setIsDownloading,
                            setProgress,
                            setStatus,
                            setPopperOpen
                        );
                        handleClose();
                    }}
                />
                <FileMenuItem
                    icon={
                        <img
                            src={PlyIconBlack}
                            alt="PLY icon"
                            style={{ width: 20, height: 20, color: "#000", verticalAlign: "middle" }}
                        />
                    }
                    label="Color 3D Files"
                    ext=".PLY"
                    onClick={() => {
                        handleDownloadFiles(
                            [row.id],
                            'ply',
                            apiRef,
                            setIsDownloading,
                            setProgress,
                            setStatus,
                            setPopperOpen
                        );
                        handleClose();
                    }}
                />
            </Menu>
        </>
    );
};
// Lichtspoor dat één keer rond de rand van de "new orders" badge loopt, met
// hetzelfde verloop als de highlight-lijn in de toolbars van de CaseDetailDrawer
// (0 → 0.35 → #FFF → 0.35 → 0). Via pathLength=100 loopt het spoor met constante
// snelheid langs de rand, ongeacht de breedte van de badge.
const SWEEP_LAYERS = [
    { len: 24, opacity: 0.35, width: 1 },
    { len: 12, opacity: 0.6, width: 1 },
    { len: 5, opacity: 1, width: 1.5 },
];
const SWEEP_DURATION = "900ms";
const SWEEP_DELAY = "150ms"; // na de inkomende Grow van de Snackbar
const sweepKeyframes = SWEEP_LAYERS.reduce((acc, { len }) => {
    acc[`@keyframes badgeSweep${len}`] = {
        from: { strokeDashoffset: len / 2 },
        to: { strokeDashoffset: len / 2 - 100 },
    };
    return acc;
}, {
    "@keyframes badgeSweepFade": {
        "0%": { opacity: 0 },
        "15%": { opacity: 1 },
        "80%": { opacity: 1 },
        "100%": { opacity: 0 },
    },
});
// shape "pill": rand van de hele badge; shape "circle": rand van het logo-rondje.
const BadgeBorderSweep = ({ shape = "pill", sx }) => {
    const Shape = shape === "circle" ? "circle" : "rect";
    const shapeProps = shape === "circle"
        ? { cx: 17, cy: 17, r: 17 }
        : { x: 0, y: 0, width: "100%", height: "100%", rx: 18, ry: 18 };
    const strokes = SWEEP_LAYERS.map(({ len, opacity, width }) => (
        <Shape
            key={len}
            {...shapeProps}
            pathLength="100"
            fill="none"
            stroke="#FFF"
            strokeOpacity={opacity}
            strokeWidth={width}
            strokeLinecap="round"
            strokeDasharray={`${len} ${100 - len}`}
            style={{ animation: `badgeSweep${len} ${SWEEP_DURATION} cubic-bezier(0.45, 0, 0.25, 1) ${SWEEP_DELAY} both` }}
        />
    ));
    return (
        <Box
            component="svg"
            aria-hidden
            sx={{
                ...sweepKeyframes,
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                overflow: "visible",
                pointerEvents: "none",
                animation: `badgeSweepFade ${SWEEP_DURATION} ease ${SWEEP_DELAY} both`,
                ...sx,
            }}
        >
            <defs>
                <filter id={`badgeSweepGlow-${shape}`} x="-50%" y="-100%" width="200%" height="300%">
                    <feGaussianBlur stdDeviation="3" />
                </filter>
            </defs>
            {/* Zachte glow onder het spoor */}
            <g filter={`url(#badgeSweepGlow-${shape})`}>{strokes}</g>
            <g>{strokes}</g>
        </Box>
    );
};

const TransparentDataGrid = ({ isDashboardVisible, isMultiCompany, onSearchModeChange, onFilterModeChange, onNewRows, pollSignal }) => {
    LicenseInfo.setLicenseKey('47d534e1070ffed2143f75a92aaa9ca1Tz0xMTMyMjcsRT0xNzc5MzIxNTk5MDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMy0yMDI0LEtWPTI='); // Use environment variable or fallback
    const { isMobile, isCompact, isPhone } = useMobile();
    // Compacte layout (telefoon/iPad staand): één case-kolom (mobileCase) i.p.v.
    // de losse kolommen, zonder checkboxes. Anders precies de desktop-kolommen
    // (ook op een iPad liggend: die is breed genoeg).
    const columnVisibilityModel = useMemo(() => (isCompact
        // Compact (mobiel): ook geen menuknopje (actions) per rij
        ? { __check__: false, image: false, dentistClinic: false, patientInfo: false, scannerAndWorktype: false, dateTime: false, actions: false, edgeSpacer: false }
        : { mobileCase: false }), [isCompact]);
    const apiRef = useGridApiRef();
    const gridRef = useRef(null);
    const scrollerRef = useRef(null);
    const [menuState, setMenuState] = useState({ open: false, anchorEl: null, row: null }); // Add this
    const [rows, setRows] = useState([]);
    const [pendingNewRows, setPendingNewRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [rowCountState, setRowCountState] = useState(0);
    const [showNewRowsNotification, setShowNewRowsNotification] = useState(false);
    const [userScrolledDown, setUserScrolledDown] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const nextRowIdRef = useRef(0);
    const pollNewRowsRef = useRef(null);
    const onNewRowsRef = useRef(onNewRows);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const fabRef = useRef(null);
    const fabContainerRef = useRef(null);
    const [page, setPage] = useState(1);
    const [atBottom, setAtBottom] = useState(false);
    const atBottomTimerRef = useRef(null);
    const [fabLeft, setFabLeft] = useState(0);
    const allRowIdsRef = useRef(new Set()); // Track all row IDs
    const [isPrepending, setIsPrepending] = useState(false);
    const pendingNewRowsRef = useRef([]);
    useEffect(() => { pendingNewRowsRef.current = pendingNewRows; }, [pendingNewRows]);
    // Key voor het lichtspoor van de badge: nieuwe waarde bij elke ophoging van de
    // teller (spoor loopt opnieuw). Bij 0 de laatste waarde houden, anders loopt het
    // spoor nog een keer terwijl de badge wegfadet.
    const badgeSweepKeyRef = useRef(0);
    if (pendingNewRows.length > 0) badgeSweepKeyRef.current = pendingNewRows.length;
    const badgeSweepKey = badgeSweepKeyRef.current;
    const rowsRef = useRef([]);
    useEffect(() => { rowsRef.current = rows; }, [rows]);
    const skeletonIdCounter = useRef(0);
    const fetchLockRef = useRef(false);
    // Verhoogd bij elke reset van de lijst (zoeken/filteren); een bijlaad-response
    // van vóór de reset wordt dan genegeerd i.p.v. achter de nieuwe resultaten geplakt
    const listGenRef = useRef(0);
    const searchInputRef = useRef(null);
    const filterButtonRef = useRef(null);
    const batchTimestamp = useMemo(() => Date.now(), [pendingNewRows]);
    const hasFetchedInitialRows = useRef(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');
    const [popperOpen, setPopperOpen] = useState(false);
    const downloadButtonRef = useRef(null);
    const [isSearching, setIsSearching] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const [activeFilters, setActiveFilters] = useState([]);
    const activeFiltersRef = useRef([]);
    useEffect(() => { activeFiltersRef.current = activeFilters; }, [activeFilters]);
    useEffect(() => { onFilterModeChange?.(activeFilters.length > 0); }, [activeFilters, onFilterModeChange]);
    const [loadingButton, setLoadingButton] = useState(null);
    //const columns = getColumns({ portals, ActionsMenuCell, isMultiCompany });
    const [lastSelectedCount, setLastSelectedCount] = useState(1); // New state for last non-zero count
    const [selectedRow, setSelectedRow] = useState(null);
    const selectedRowRef = useRef(null);
    useEffect(() => { selectedRowRef.current = selectedRow; }, [selectedRow]);

    const updateRowById = useCallback((id, updates) => {
        const shouldRemove =
            (activeFilters.includes('Not Accepted') && updates.status === 'accepted') ||
            // 'info_requested' bestaat niet als status; het staat in de aparte kolom info_request_status
            (activeFilters.includes('Info Requested') && updates.info_request_status !== undefined && updates.info_request_status !== 'requested');

        if (shouldRemove) {
            setRows(prev => prev.filter(r => r.id !== id));
            setPendingNewRows(prev => prev.filter(r => r.id !== id));
            apiRef.current.updateRows([{ id, _action: 'delete' }]);
        } else {
            setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
            setPendingNewRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
        }
    }, [activeFilters]);

    const columns = useMemo(
        () =>
            getColumns({
                portals,
                apiRef,
                updateRowById,
                ActionsMenuCell: (props) => (
                    <ActionsMenuCell
                        {...props}
                        apiRef={apiRef}
                        setIsDownloading={setIsDownloading}
                        setProgress={setProgress}
                        setStatus={setStatus}
                        setPopperOpen={setPopperOpen}
                        onOpenDetails={(row) => {
                            const currentIndex = allRowsRef.current.findIndex(r => r.id === row.id);
                            setSelectedRow({ ...row, index: currentIndex });
                            setShouldHide(true);
                        }}
                        onUpdateRow={updateRowById}
                    />
                ),
                isMultiCompany,
            }),
        [apiRef, updateRowById, setIsDownloading, setProgress, setStatus, setPopperOpen, isMultiCompany]
    );



    // Initialize scrollerRef with retry
    useEffect(() => {
        let retryCount = 0;
        const maxRetries = 10;
        const retryInterval = 100;
        const initializeScroller = () => {
            if (gridRef.current && !scrollerRef.current) {
                scrollerRef.current = gridRef.current.querySelector(
                    ".MuiDataGrid-virtualScroller"
                );
                if (scrollerRef.current) {
                    console.log("scrollerRef initialized successfully");
                }
            }
            if (!scrollerRef.current && retryCount < maxRetries) {
                retryCount++;
                console.log(`Retrying scrollerRef initialization (${retryCount}/${maxRetries})`);
                setTimeout(initializeScroller, retryInterval);
            } else if (!scrollerRef.current) {
                console.error("Failed to initialize scrollerRef after max retries");
            }
        };
        initializeScroller();
        return () => {
            scrollerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (isDashboardVisible) {
            setSearchTerm("");
        } else if (initialFetchDone) {
            pollNewRowsRef.current?.();
        }
    }, [isDashboardVisible, initialFetchDone]);

    // Forward wheel events to scroller
    useEffect(() => {
        let cleanup = () => { };
        const tryAttach = () => {
            const fab = fabRef.current;
            const fabContainer = fabContainerRef.current;
            const scroller = scrollerRef.current;
            if (!fab || !fabContainer || !scroller) {
                setTimeout(tryAttach, 100);
                return;
            }
            const handleWheel = (event) => {
                if (scrollerRef.current) {
                    scrollerRef.current.scrollTop += event.deltaY;
                }
            };
            fab.addEventListener("wheel", handleWheel, { passive: true });
            fabContainer.addEventListener("wheel", handleWheel, { passive: true });
            cleanup = () => {
                fab.removeEventListener("wheel", handleWheel);
                fabContainer.removeEventListener("wheel", handleWheel);
            };
        };
        tryAttach();
        return () => cleanup();
    }, [selectedRowIds.length > 0]);
    useEffect(() => {
        if (isDashboardVisible) {
            setShowNewRowsNotification(false);
        }
    }, [isDashboardVisible]);
    // Handle row selection changes
    useEffect(() => {
        const handleSelectionChange = () => {
            const selectedIds = Array.from(apiRef.current.getSelectedRows().keys());
            setSelectedRowIds(selectedIds);
        };
        const unsubscribe = apiRef.current.subscribeEvent(
            "rowSelectionChange",
            handleSelectionChange
        );
        return () => unsubscribe();
    }, [apiRef]);
    useEffect(() => {
        if (selectedRowIds.length > 0) {
            setLastSelectedCount(selectedRowIds.length);
        }
    }, [selectedRowIds.length]);

    useRowGlowBlocks(apiRef, ROW_GLOW_BLOCKS);

    // Track scroll position
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const handleScroll = () => {
            if (scroller.scrollTop > 20) {
                setUserScrolledDown(true);
                setShowScrollToTop(true);
            } else if (scroller.scrollTop <= 10) {
                setUserScrolledDown(false);
                setShowScrollToTop(false);
            }
            const { rows: r, rowCountState: total } = scrollStateRef.current;
            const allLoaded = r.length >= total;
            const isScrollable = scroller.scrollHeight > scroller.clientHeight + 56;
            const isAtBottom =
                allLoaded &&
                (isScrollable
                    ? scroller.scrollHeight - scroller.scrollTop <= scroller.clientHeight + 2
                    : scroller.clientHeight - scroller.scrollHeight < 56);
            if (atBottomTimerRef.current) {
                clearTimeout(atBottomTimerRef.current);
                atBottomTimerRef.current = null;
            }
            if (isAtBottom) {
                atBottomTimerRef.current = setTimeout(() => {
                    atBottomTimerRef.current = null;
                    setAtBottom(true);
                }, 150);
            } else {
                setAtBottom(false);
            }
        };
        scroller.addEventListener("scroll", handleScroll);
        return () => scroller.removeEventListener("scroll", handleScroll);
    }, []);

    // Handle atBottom for non-scrollable grid (scroll event never fires)
    useEffect(() => {
        requestAnimationFrame(() => {
            const scroller = scrollerRef.current;
            if (!scroller) return;
            const isScrollable = scroller.scrollHeight > scroller.clientHeight + 56;
            if (isScrollable) {
                setAtBottom(false); // reset stale state; scroll event will take over
                return;
            }
            const allLoaded = rows.length >= rowCountState && rowCountState > 0;
            if (!allLoaded) { setAtBottom(false); return; }
            const dataRows = scroller.querySelectorAll('.MuiDataGrid-row');
            if (!dataRows.length) { setAtBottom(false); return; }
            const lastRowBottom = dataRows[dataRows.length - 1].getBoundingClientRect().bottom;
            const searchboxTop = window.innerHeight - 56;
            setAtBottom(lastRowBottom > searchboxTop);
        });
    }, [rows, rowCountState]);

    // Update FAB position
    useEffect(() => {
        const updateFabPosition = () => {
            if (gridRef.current) {
                const gridRect = gridRef.current.getBoundingClientRect();
                const desiredLeft = gridRect.right + 60;
                const maxLeft = window.innerWidth - 60;
                const clampedLeft = Math.min(desiredLeft, maxLeft);
                setFabLeft(clampedLeft);
            }
        };
        updateFabPosition();
        window.addEventListener("resize", updateFabPosition);
        return () => window.removeEventListener("resize", updateFabPosition);
    }, []);

    // Initial fetch
    useEffect(() => {
        if (hasFetchedInitialRows.current) {
            console.log("Skipping duplicate fetchInitialRows");
            return;
        }
        hasFetchedInitialRows.current = true;

        const fetchInitialRows = async () => {
            setIsLoading(true);
            try {
                const response = await apiClient.get('/cases', {
                    params: {
                        page: 1,
                        pageSize: PAGE_SIZE,
                        organizationId: parseInt(localStorage.getItem('organization_id')),
                    },
                });
                console.log("Backend response for initial fetch:", response.data);
                const validRows = response.data.rows.filter(row => row && typeof row === 'object' && row.id != null);
                if (response.data.rows.length !== validRows.length) {
                    console.warn(`Filtered ${response.data.rows.length - validRows.length} invalid rows from initial fetch`);
                }
                const initialRows = validRows
                    .map(row => mapDatabaseRowToGridRow(row, Date.now()))
                    .filter(row => row !== null);
                console.log("Initial rows fetched:", initialRows.map(r => r.id));
                setRows(initialRows);
                setRowCountState(response.data.totalRows);
                setPage(2);
                nextRowIdRef.current = initialRows.length;
            } catch (error) {
                console.error("Error fetching initial rows:", error);
            } finally {
                setIsLoading(false);
                setInitialFetchDone(true);
            }
        };

        fetchInitialRows();
    }, []); // Keep empty deps


    // Fetch more rows on scroll
    const fetchMoreRows = useCallback(async () => {
        // Pending rijen (badge) zitten al in rowCountState maar nog niet in rows; tel ze mee,
        // anders denkt de grid dat er nog een pagina is en blijft hij leeg bijladen (bv. lege lijst)
        const realRowCount = rows.filter(r => !r.isSkeleton).length + pendingNewRowsRef.current.length;
        if (isLoading || realRowCount >= rowCountState || fetchLockRef.current) {
            return false;
        }
        fetchLockRef.current = true;
        const gen = listGenRef.current;
        const skeletonStartIndex = rows.length;
        // Laagste id in de lijst = cursor voor de volgende pagina (lijst staat op id aflopend)
        const lowestId = rows.reduce((min, r) => (r.isSkeleton ? min : Math.min(min, Number(r.id) || Infinity)), Infinity);
        const useKeyset = USE_KEYSET_PAGING && Number.isFinite(lowestId);
        const skipCount = useKeyset || (activeFiltersRef.current.length > 0 && !debouncedSearchTermRef.current);
        const pageSize = useKeyset ? MORE_PAGE_SIZE : PAGE_SIZE;
        // Generate stable skeleton IDs
        const skeletonRows = Array.from({ length: pageSize }, () => {
            const id = `skeleton-${skeletonIdCounter.current++}`;
            return {
                id,
                isSkeleton: true,
                name: "",
                dentist: "",
                clinic: "",
                patientName: "",
                birthday: "",
                ref: "",
                scannerBrand: 1,
                worktype: "",
                receiver: "",
                date: "",
            };
        });
        console.log("Adding skeleton rows:", skeletonRows.map(r => r.id));
        setRows(prev => [...prev, ...skeletonRows]);
        setIsLoading(true);
        try {
            const response = await apiClient.get('/cases', {
                params: {
                    page,
                    pageSize,
                    organizationId: parseInt(localStorage.getItem('organization_id')),
                    search: debouncedSearchTermRef.current || undefined,
                    statusFilters: activeFiltersRef.current.length > 0 ? activeFiltersRef.current.join(',') : undefined,
                    beforeId: useKeyset ? lowestId : undefined,
                    skipCount: skipCount ? 'true' : undefined,
                },
            });
            if (gen !== listGenRef.current) return false; // lijst is intussen gereset
            const newRows = response.data.rows
                .map(row => mapDatabaseRowToGridRow(row, batchTimestamp))
                .filter(row => row !== null);
            console.log("Fetched new rows:", newRows.map(r => r.id));
            // Skeletons op vlag verwijderen (niet op index): tussentijds geprepende rijen
            // schuiven de indices op, en dan zouden echte rijen weggeknipt worden
            setRows(prev => {
                const real = prev.filter(r => !r.isSkeleton);
                const ids = new Set(real.map(r => String(r.id)));
                const appended = newRows
                    .filter(r => !ids.has(String(r.id)))
                    .map(r => ({ ...r, _key: `row-${r.id}-${Date.now()}`, justAdded: false }));
                return [...real, ...appended];
            });
            if (useKeyset) {
                // Geen telling: einde van de lijst herkennen aan een korte pagina. Op het
                // aantal uit de response, want newRows kan door dedup korter zijn.
                if (response.data.rows.length < pageSize) {
                    setRowCountState(skeletonStartIndex + newRows.length + pendingNewRowsRef.current.length);
                }
            } else if (skipCount) {
                // Count was skipped — detect end-of-data from a short page
                if (newRows.length < PAGE_SIZE) {
                    setRowCountState(skeletonStartIndex + newRows.length);
                }
            } else {
                setRowCountState(response.data.totalRows);
            }
            setPage(prev => prev + 1);
            nextRowIdRef.current += newRows.length;
            return true;
        } catch (error) {
            console.error("Error fetching rows:", error);
            // Remove skeletons on error
            if (gen === listGenRef.current) setRows(prev => prev.filter(r => !r.isSkeleton));
            return false;
        } finally {
            // Na een reset beheert zoeken/filteren zelf de loading-state
            if (gen === listGenRef.current) setIsLoading(false);
            fetchLockRef.current = false;
        }
    }, [isLoading, page, rows.length, rowCountState, batchTimestamp]);



    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 400);
    const debouncedSearchTermRef = useRef('');
    useEffect(() => { debouncedSearchTermRef.current = debouncedSearchTerm; }, [debouncedSearchTerm]);
    const [hasSearchedOnce, setHasSearchedOnce] = useState(false); // NEW: Track if user has searched
    const searchAbortRef = useRef(null);


    useEffect(() => {
        const isSearchMode = debouncedSearchTerm !== "";
        onSearchModeChange(isSearchMode);
    }, [debouncedSearchTerm, onSearchModeChange]);
    // Search functionality - ONLY triggers when user actually searches
    useEffect(() => {
        // Don't run on mount or before initial data loads
        if (!hasFetchedInitialRows.current) {
            return;
        }

        // If search is empty and we haven't searched yet, do nothing
        if (!debouncedSearchTerm && !hasSearchedOnce) {
            console.log('[SEARCH] No search yet, skipping');
            return;
        }

        // Mark that we've searched at least once
        if (debouncedSearchTerm) {
            setHasSearchedOnce(true);
        }

        console.log('[SEARCH] Executing search for:', debouncedSearchTerm || '(all results)');

        // Reset state
        listGenRef.current++;
        setPage(1);
        setRows([]);
        setPendingNewRows([]);
        setRowCountState(0);
        setSelectedRowIds([]);
        allRowIdsRef.current.clear();

        const fetchFilteredRows = async () => {
            // Cancel vorige request als die nog loopt
            if (searchAbortRef.current) {
                searchAbortRef.current.abort();
            }
            const controller = new AbortController();
            searchAbortRef.current = controller;

            setIsSearching(debouncedSearchTerm !== '');
            setIsLoading(true);
            try {
                const filterOnly = activeFiltersRef.current.length > 0 && !debouncedSearchTerm;
                const response = await apiClient.get('/cases', {
                    params: {
                        page: 1,
                        pageSize: PAGE_SIZE,
                        organizationId: parseInt(localStorage.getItem('organization_id')),
                        search: debouncedSearchTerm || undefined,
                        statusFilters: activeFiltersRef.current.length > 0 ? activeFiltersRef.current.join(',') : undefined,
                        skipCount: filterOnly ? 'true' : undefined,
                    },
                    signal: controller.signal,  // ← koppel abort signal
                });

                // Als deze request geaborteerd is, negeer de response
                if (controller.signal.aborted) return;

                const validRows = response.data.rows.filter(row => row && typeof row === 'object' && row.id != null);
                const newRows = validRows
                    .map(row => mapDatabaseRowToGridRow(row, Date.now()))
                    .filter(row => row !== null);

                setRows(newRows);
                setRowCountState(filterOnly ? 999999 : response.data.totalRows);
                setPage(2);
                nextRowIdRef.current = newRows.length;
                handleScrollToTop("instant");
            } catch (error) {
                // Negeer abort errors — die zijn verwacht
                if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
                console.error('[SEARCH] Error:', error);
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                    setIsSearching(false);
                }
            }
        };

        fetchFilteredRows();
    }, [debouncedSearchTerm]);

    const filterAbortRef = useRef(null);
    const isFirstFilterRender = useRef(true);

    useEffect(() => {
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false;
            return;
        }
        if (!hasFetchedInitialRows.current) return;

        listGenRef.current++;
        if (filterAbortRef.current) filterAbortRef.current.abort();
        const controller = new AbortController();
        filterAbortRef.current = controller;

        setPendingNewRows([]);
        setSelectedRowIds([]);
        allRowIdsRef.current.clear();

        const fetchWithFilter = async () => {
            setIsLoading(true);
            try {
                const response = await apiClient.get('/cases', {
                    params: {
                        page: 1,
                        pageSize: PAGE_SIZE,
                        organizationId: parseInt(localStorage.getItem('organization_id')),
                        search: debouncedSearchTermRef.current || undefined,
                        statusFilters: activeFilters.length > 0 ? activeFilters.join(',') : undefined,
                        skipCount: activeFilters.length > 0 && !debouncedSearchTermRef.current ? 'true' : undefined,
                    },
                    signal: controller.signal,
                });
                if (controller.signal.aborted) return;
                const validRows = response.data.rows.filter(row => row && typeof row === 'object' && row.id != null);
                const newRows = validRows.map(row => mapDatabaseRowToGridRow(row, Date.now(), true)).filter(row => row !== null);
                setRows(newRows);
                setRowCountState(
                    activeFilters.length > 0 && !debouncedSearchTermRef.current
                        ? 999999
                        : response.data.totalRows
                );
                setPage(2);
                nextRowIdRef.current = newRows.length;
                handleScrollToTop("instant");
            } catch (error) {
                if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
                console.error('[FILTER] Error:', error);
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };

        fetchWithFilter();
    }, [activeFilters]);

    const [debouncedPollNewRows] = useDebounce(() => {
        pollNewRows();
    }, 10000); // 10-second debounce
    // Polling for new rows using maxId
    const pollNewRows = useCallback(async () => {

        if (debouncedSearchTerm) {
            console.log("Polling skipped: search active");
            return;
        }
        if (isPrepending) {
            console.log("Polling skipped: prepending in progress");
            return;
        }
        try {
            const allIds = [
                ...rows.map(r => String(r.id)),
                ...pendingNewRows.map(r => String(r.id)),
            ].filter(id => !id.startsWith('skeleton-'));
            const maxId = allIds.length > 0 ? Math.max(...allIds.map(id => parseInt(id, 10))) : 0;
            console.log("Polling with maxId:", maxId);
            const response = await apiClient.get('/cases', {
                params: {
                    page: 1,
                    pageSize: PAGE_SIZE,
                    sinceId: maxId,
                    organizationId: parseInt(localStorage.getItem('organization_id')),
                    statusFilters: activeFiltersRef.current.length > 0 ? activeFiltersRef.current.join(',') : undefined,
                },
            });
            const validRows = response.data.rows.filter(row => row && typeof row === 'object' && row.id != null);
            if (response.data.rows.length !== validRows.length) {
                console.warn(`Filtered ${response.data.rows.length - validRows.length} invalid rows from polling`);
            }
            const newRows = validRows
                .map(row => mapDatabaseRowToGridRow(row, batchTimestamp))
                .filter(row => row !== null);
            console.log("Polled new rows:", newRows.map(r => r.id));
            // Dedup buiten de state-updater: in StrictMode draait een updater twee keer,
            // waardoor rowCountState dubbel opgehoogd werd (en de grid onterecht ging bijladen)
            const existingIds = new Set([
                ...rowsRef.current.map(r => String(r.id)),
                ...pendingNewRowsRef.current.map(r => String(r.id)),
            ]);
            const uniqueNewRows = newRows.filter(row => {
                const id = String(row.id);
                if (Number(row.id) <= maxId || existingIds.has(id)) return false;
                existingIds.add(id);
                return true;
            });
            if (uniqueNewRows.length > 0) {
                console.log("Adding unique new rows to pendingNewRows:", uniqueNewRows.map(r => r.id));
                uniqueNewRows.forEach(row => allRowIdsRef.current.add(String(row.id)));
                pendingNewRowsRef.current = [...uniqueNewRows, ...pendingNewRowsRef.current];
                setPendingNewRows(currentPending => {
                    const pendingIds = new Set(currentPending.map(r => String(r.id)));
                    return [...uniqueNewRows.filter(r => !pendingIds.has(String(r.id))), ...currentPending];
                });
                setRowCountState(prev => prev + uniqueNewRows.length);
                onNewRowsRef.current?.();
            }
        } catch (error) {
            console.error("Error polling new rows:", error);
        }
    }, [rows, pendingNewRows, isPrepending, debouncedSearchTerm]);

    useEffect(() => { pollNewRowsRef.current = pollNewRows; }, [pollNewRows]);
    useEffect(() => { onNewRowsRef.current = onNewRows; }, [onNewRows]);

    useEffect(() => {
        if (!pollSignal || !initialFetchDone || isDashboardVisible) return;
        pollNewRowsRef.current?.();
    }, [pollSignal]);

    // Poll every minute
    useEffect(() => {
        if (!initialFetchDone) {
            console.log("Polling delayed: waiting for initial fetch to complete");
            return;
        }
        console.log("Starting polling with", rows.length, "rows");
        const interval = setInterval(pollNewRows, 60000);
        return () => clearInterval(interval);
    }, [pollNewRows, initialFetchDone]);
    // Prepend new rows
    const prependNewRows = useCallback(() => {
        if (isPrepending || pendingNewRows.length === 0) {
            console.log("Prepend blocked: isPrepending =", isPrepending, ", pendingNewRows.length =", pendingNewRows.length);
            return;
        }
        setIsPrepending(true);
        // Create a snapshot of pending rows to avoid race conditions
        const rowsToAdd = [...pendingNewRows];
        // Clear pending rows immediately to prevent duplicates
        setPendingNewRows([]);
        const sortedNewRows = rowsToAdd
            .sort((a, b) => Number(b.id) - Number(a.id))
            .map(row => ({
                ...row,
                justAdded: true,
            }));
        setRows(prevRows => {
            // Update allRowIdsRef first with existing rows
            const currentIds = new Set(prevRows.map(row => String(row.id)));
            // Filter out any duplicates from sortedNewRows
            const uniqueNewRows = sortedNewRows.filter(row => {
                const id = String(row.id);
                if (currentIds.has(id)) {
                    console.warn("Duplicate ID prevented during prepend:", id);
                    return false;
                }
                currentIds.add(id);
                allRowIdsRef.current.add(id);
                return true;
            });
            const merged = [...uniqueNewRows, ...prevRows];
            console.log("Prepended rows:", uniqueNewRows.map(r => r.id));
            // Let React handle the update naturally - don't force it with apiRef
            return merged;
        });
        setTimeout(() => {
            setRows(prevRows =>
                prevRows.map(row => ({
                    ...row,
                    justAdded: false,
                }))
            );
            setIsPrepending(false); // Reset prepending flag
            // Scroll to top after React has finished updating
            apiRef.current.scrollToIndexes({ rowIndex: 0 });
        }, 400);
        setShowNewRowsNotification(false);
    }, [pendingNewRows, apiRef, isPrepending]);
    // Memoized version of prependNewRows with guard
    const prependNewRowsOnce = useCallback(() => {
        if (isPrepending) return;
        setIsPrepending(true);
        prependNewRows(); // You should reset isPrepending inside prependNewRows when done
    }, [prependNewRows, isPrepending]);
    // Handle pending new rows
    useEffect(() => {
        if (pendingNewRows.length === 0 || !scrollerRef.current || isPrepending) return;
        const timeout = setTimeout(() => {
            if (isPrepending || pendingNewRows.length === 0) return;
            const scrollTop = scrollerRef.current.scrollTop;
            const scrollHeight = scrollerRef.current.scrollHeight;
            const clientHeight = scrollerRef.current.clientHeight;
            const isScrollable = scrollHeight > clientHeight;
            const actuallyAtTop = Math.floor(scrollTop) <= 5;
            if (isScrollable && actuallyAtTop) {
                prependNewRowsOnce();
            } else if (!userScrolledDown) {
                prependNewRowsOnce();
            } else {
                setShowNewRowsNotification(true);
            }
        }, 200);
        return () => clearTimeout(timeout);
    }, [pendingNewRows, prependNewRowsOnce, userScrolledDown, isPrepending]);
    // ROW NAVIGATIE VANUIT DETAILS PAGINA
    const [allRows, setAllRows] = useState([]);
    const allRowsRef = useRef([]);
    const allRowsNavRef = useRef([]);
    const rowsWithHeadersRef = useRef([]);
    const [keyboardActiveId, setKeyboardActiveId] = useState(null);
    const keyboardActiveIdRef = useRef(null);
    const isDashboardVisibleRef = useRef(isDashboardVisible);
    const shouldHideRef = useRef(false);
    useEffect(() => { isDashboardVisibleRef.current = isDashboardVisible; }, [isDashboardVisible]);

    useEffect(() => {
        const flattened = [
            ...pendingNewRows, // Altijd bovenaan: nieuwe rijen (hogere IDs)
            ...rows
        ].filter(row => row && !row.isSkeleton && row.id); // Filter skeletons
        //  const sorted = flattened.sort((a, b) => Number(a.id) - Number(b.id)); // Oplopend op ID
        const sorted = flattened.sort((a, b) => Number(b.id) - Number(a.id)); // Hoogste ID eerst (nieuwste bovenaan)
        setAllRows(sorted);
        allRowsNavRef.current = sorted;
        console.log("Updated allRows: pending =", pendingNewRows.length, ", total =", sorted.length);
        console.log("Pending IDs:", pendingNewRows.map(r => r.id));
    }, [rows, pendingNewRows]);

    const rowsWithHeaders = useMemo(() => {
        if (!SECTION_HEADERS_ENABLED) return allRows;
        const todayDateStr = new Date().toDateString();
        const todayRows = allRows.filter(r => r.date && new Date(r.date).toDateString() === todayDateStr);
        const earlierRows = allRows.filter(r => !r.date || new Date(r.date).toDateString() !== todayDateStr);
        const result = [];
        if (todayRows.length > 0) {
            result.push({ id: '__header_today', isSectionHeader: true, label: 'Today' });
            result.push(...todayRows);
        }
        if (earlierRows.length > 0) {
            result.push({ id: '__header_earlier', isSectionHeader: true, label: 'Earlier' });
            result.push(...earlierRows);
        }
        return result;
    }, [allRows]);

    useEffect(() => { rowsWithHeadersRef.current = rowsWithHeaders; }, [rowsWithHeaders]);

    const handleRowClick = useCallback((params) => {
        const currentIndex = allRows.findIndex(row => row.id === params.row.id);
        const isPending = pendingNewRows.some(r => r.id === params.row.id);
        console.log("Clicked row ID:", params.row.id, ", index:", currentIndex, ", isPending:", isPending);
        keyboardActiveIdRef.current = params.row.id;
        setKeyboardActiveId(params.row.id);
        setSelectedRow({ ...params.row, index: currentIndex });
        setShouldHide(true);
    }, [allRows, pendingNewRows]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
            if (isDashboardVisibleRef.current || shouldHideRef.current) return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            const navigableRows = allRowsNavRef.current;
            if (navigableRows.length === 0) return;
            e.preventDefault();

            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                const currentIndex = navigableRows.findIndex(r => r.id === keyboardActiveIdRef.current);
                let nextIndex;
                if (e.key === 'ArrowDown') {
                    nextIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, navigableRows.length - 1);
                } else {
                    nextIndex = currentIndex === -1 ? 0 : Math.max(currentIndex - 1, 0);
                }
                const nextRow = navigableRows[nextIndex];
                keyboardActiveIdRef.current = nextRow.id;
                setKeyboardActiveId(nextRow.id);

                const gridRowIndex = rowsWithHeadersRef.current.findIndex(r => r.id === nextRow.id);
                if (gridRowIndex >= 0) {
                    try { apiRef.current.scrollToIndexes({ rowIndex: gridRowIndex }); } catch (_) {}
                }
            }

            if (e.key === 'Enter' && keyboardActiveIdRef.current) {
                const row = allRowsNavRef.current.find(r => r.id === keyboardActiveIdRef.current);
                if (row) {
                    const index = allRowsNavRef.current.findIndex(r => r.id === keyboardActiveIdRef.current);
                    setSelectedRow({ ...row, index });
                    setShouldHide(true);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // In DataGrid-component
    const [autoPrependPending, setAutoPrependPending] = useState(true); // Optioneel: toggle voor auto-load
    // Hulpfunctie om pending rows naar grid te prependen (roep aan als nodig)
    const prependPendingToGrid = useCallback(async () => {
        if (pendingNewRows.length === 0) return;
        setRows(prev => [...pendingNewRows, ...prev]); // Prepend aan begin van rows
        setPendingNewRows([]); // Leeg pending na preload
        setRowCountState(prev => prev - pendingNewRows.length + pendingNewRows.length); // Geen change, maar consistent
        nextRowIdRef.current += pendingNewRows.length; // Update je ref
        console.log("Prepended", pendingNewRows.length, "pending rows to grid");
    }, [pendingNewRows]);




    const [shouldAutoSelectNext, setShouldAutoSelectNext] = useState(false);
    const pendingSelectIndexRef = useRef(null);
    useEffect(() => {
        if (shouldAutoSelectNext && pendingSelectIndexRef.current !== null) {
            const nextIndex = pendingSelectIndexRef.current;
            if (nextIndex < allRows.length && allRows[nextIndex]) {
                const nextRow = { ...allRows[nextIndex], index: nextIndex };
                setSelectedRow(nextRow);
                console.log("Auto-selected after fetch:", nextRow.id);
            }
            setShouldAutoSelectNext(false);
            pendingSelectIndexRef.current = null;
        }
    }, [allRows, shouldAutoSelectNext]);

    const handleOlderRow = useCallback(async (currentIndex) => {
        // De meegegeven currentIndex kan verouderd zijn: als er tussentijds nieuwe
        // cases zijn binnengekomen (pendingNewRows) schuift allRows op en klopt de
        // index niet meer. Zoek de huidige rij daarom opnieuw op via zijn id.
        const realIndex = allRows.findIndex(r => r.id === selectedRowRef.current?.id);
        // Rij nog aanwezig (evt. verschoven): één stap ouder vanaf de echte positie.
        // Rij niet meer aanwezig (bv. weggefilterd): alle indices ≥ currentIndex zijn
        // al één omlaag geschoven, dus currentIndex zelf is al de volgende oudere rij.
        const startIndex = realIndex !== -1 ? realIndex + 1 : currentIndex;

        const nearEnd = startIndex >= allRows.length - 5;
        const realCount = allRows.filter(r => !r.isSkeleton).length;

        if (nearEnd && realCount < rowCountState && !fetchLockRef.current) {
            console.log("Bijna aan het einde, proactief laden...");
            fetchMoreRows();
        }

        if (startIndex < allRows.length) {
            // gewone navigatie
            if (allRows[startIndex]) {
                setSelectedRow({ ...allRows[startIndex], index: startIndex });
            }
        } else {
            // echt aan het einde: wacht op fetch
            const success = await fetchMoreRows();
            if (success) {
                pendingSelectIndexRef.current = startIndex;
                setShouldAutoSelectNext(true);
            }
        }
    }, [allRows, pendingNewRows, autoPrependPending, prependPendingToGrid, fetchMoreRows, rowCountState]);

    // CASE_PREFETCH: de volgende (oudere) en vorige (nieuwere) case van de open
    // case, zodat de drawer daar alvast scans/IOS Form van kan laden
    const neighborRows = useMemo(() => {
        if (!CASE_PREFETCH || !selectedRow) return null;
        const i = allRows.findIndex(r => r.id === selectedRow.id);
        if (i === -1) return null;
        return [allRows[i + 1], allRows[i - 1]].filter(r => r && !r.isSkeleton);
    }, [allRows, selectedRow]);

    const handleNewerRow = useCallback((currentIndex) => {
        // Zelfde probleem als bij handleOlderRow: currentIndex kan verouderd zijn
        // doordat er tussentijds nieuwe cases zijn binnengekomen. Zoek de huidige
        // rij opnieuw op via zijn id, zodat "previous" altijd precies één rij
        // nieuwer laadt dan de rij die nu écht open staat, en geen cases overslaat.
        const realIndex = allRows.findIndex(r => r.id === selectedRowRef.current?.id);
        const baseIndex = realIndex !== -1 ? realIndex : currentIndex;
        if (baseIndex > 0) {
            const prevIndex = baseIndex - 1;
            const prevRow = { ...allRows[prevIndex], index: prevIndex };
            // Pending rows zitten bovenaan, dus previous gaat zelden naar pending, maar check anyway
            const isPrevPending = pendingNewRows.some(r => r.id === allRows[prevIndex].id);
            if (isPrevPending && autoPrependPending) {
                prependPendingToGrid(); // Async niet nodig voor previous
            }
            setSelectedRow(prevRow);
        }
    }, [allRows, pendingNewRows, autoPrependPending, prependPendingToGrid]);

    // Refs so the stable scroll handler always reads current values without re-registering
    const scrollStateRef = useRef({ rows, isLoading, rowCountState });
    useEffect(() => { scrollStateRef.current = { rows, isLoading, rowCountState }; }, [rows, isLoading, rowCountState]);
    const fetchMoreRowsRef = useRef(fetchMoreRows);
    useEffect(() => { fetchMoreRowsRef.current = fetchMoreRows; }, [fetchMoreRows]);

    // Stable scroll listener — attaches once, reads live values via refs
    useEffect(() => {
        const gridContainer = gridRef.current;
        if (!gridContainer) return;
        let scroller = null;

        const handleScroll = () => {
            if (!scroller) return;
            const { rows: r, isLoading: loading, rowCountState: total } = scrollStateRef.current;
            const realCount = r.filter(row => !row.isSkeleton).length;
            if (loading || realCount >= total || fetchLockRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scroller;
            if (scrollTop + clientHeight >= scrollHeight - clientHeight * LOAD_AHEAD_SCREENS) {
                fetchMoreRowsRef.current();
            }
        };

        const tryAttach = () => {
            scroller = gridContainer.querySelector(".MuiDataGrid-virtualScroller");
            if (!scroller) { setTimeout(tryAttach, 100); return; }
            scroller.addEventListener('scroll', handleScroll);
        };

        tryAttach();
        return () => { if (scroller) scroller.removeEventListener('scroll', handleScroll); };
    }, []);


    // After rows or loading state changes, check if we're already at the threshold
    // (handles the case where new rows don't produce a scroll event)
    useEffect(() => {
        if (isLoading || fetchLockRef.current) return;
        const scroller = gridRef.current?.querySelector(".MuiDataGrid-virtualScroller");
        if (!scroller) return;
        const realCount = rows.filter(r => !r.isSkeleton).length;
        if (realCount >= rowCountState) return;
        const { scrollTop, scrollHeight, clientHeight } = scroller;
        if (scrollTop + clientHeight >= scrollHeight - clientHeight * LOAD_AHEAD_SCREENS) {
            fetchMoreRows();
        }
    }, [rows, isLoading, rowCountState, fetchMoreRows]);





    // Sync row ID map after rows load
    useEffect(() => {
        const currentIds = new Set(
            rows
                .filter(row => !row.isSkeleton)
                .map(row => String(row.id))
                .concat(pendingNewRows.map(row => String(row.id)))
        );
        allRowIdsRef.current = currentIds;
        console.log("allRowIdsRef synced with", currentIds.size, "IDs");
    }, [rows, pendingNewRows]);
    // // Handle scroll to top
    // const handleScrollToTop = useCallback(() => {
    // apiRef.current.scrollToIndexes({ rowIndex: 0 });
    // requestAnimationFrame(() => {
    // const mainEl = document.querySelector('.MuiDataGrid-virtualScroller');
    // if (mainEl) {
    // mainEl.dispatchEvent(new Event('scroll'));
    // }
    // // OR, if needed:
    // apiRef.current?.unstable_requestPipeProcessing?.();
    // });
    // setShowScrollToTop(false);
    // }, [apiRef]);
    const handleScrollToTop = useCallback((behavior = "smooth") => {
        const scroller = document.querySelector(".MuiDataGrid-virtualScroller");
        if (scroller) {
            scroller.scrollTo({ top: 0, behavior });
        }
    }, []);
    // Effect to update showNewRowsNotification
    useEffect(() => {
        if (pendingNewRows.length === 0) {
            setShowNewRowsNotification(false);
        } else if (!isDashboardVisible) {
            setShowNewRowsNotification(true);
        }
        if (isDashboardVisible) {
            setShowNewRowsNotification(false);
        }
    }, [isDashboardVisible, pendingNewRows.length]); // ← Use .length for precision
    // Handle Popper visibility
    useEffect(() => {
        if (status) {
            setPopperOpen(true);
            const timer = setTimeout(() => setPopperOpen(false), 6000);
            return () => clearTimeout(timer);
        }
    }, [status]);
    const [consoleOpen, setConsoleOpen] = useState(false);
    const formatBirthday = (value) => {
        if (!value) return "";
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const [, year, month, day] = isoMatch;
            return `${day}/${month}/${year}`;
        }
        return value.replace(/-/g, "/");
    };
    const mapDatabaseRowToGridRow = (dbRow, batchTimestamp, skipDedup = false) => {
        const rowId = String(dbRow.id);
        if (!skipDedup && allRowIdsRef.current.has(rowId)) return null;
        allRowIdsRef.current.add(rowId);
        const isNew = !allRowIdsRef.current.has(rowId);
        let worktypesArray = [];
        if (dbRow.worktypes) {
            try {
                worktypesArray = typeof dbRow.worktypes === 'string'
                    ? JSON.parse(dbRow.worktypes)
                    : Array.isArray(dbRow.worktypes) ? dbRow.worktypes : [];
            } catch (e) {
                console.warn('Failed to parse worktypes JSON:', e);
                worktypesArray = [];
            }
        }
        let administrativeArray = [];
        if (dbRow.administrative) {
            try {
                administrativeArray = typeof dbRow.administrative === 'string'
                    ? JSON.parse(dbRow.administrative)
                    : Array.isArray(dbRow.administrative) ? dbRow.administrative : [];
            } catch (e) {
                console.warn('Failed to parse administrative JSON:', e);
                administrativeArray = [];
            }
        }

        return {
            id: rowId,
            _key: `row-${dbRow.id}-${dbRow.received || batchTimestamp}`,
            name: `Case ${dbRow.id}`,
            justAdded: isNew,
            dentist:
                role === "Demo" ? getRandomDentist() : dbRow.dentist || "",
            clinic:
                role === "Demo" ? getRandomClinic() : dbRow.clinic || "",
            patientName:
                role === "Demo" ? getRandomPatient() : dbRow.patientname || "",
            birthday: formatBirthday(dbRow.patientdateofbirth),
            ref: dbRow.patientrefnr || "",
            scannerBrand: dbRow.portalid || 1,
            worktype: dbRow.level1 || "Unknown",
            date: dbRow.received || new Date().toISOString(),
            orderform: dbRow.filelink || "",
            stlfiles: dbRow.scanfiles || "",
            miscfiles: dbRow.miscellaneousfiles || "",
            receiver: dbRow.shortname || "",
            remark: dbRow.remark || "",
            worktypes: worktypesArray,
            deliverydate: dbRow.deliverydate || "",
            administrative: administrativeArray,
            status: dbRow.status || "new",
            flag: dbRow.status === "accepted" ? 1 : 0,
            configuredPortalId: dbRow.configured_portal_id ?? null,
            externalId: dbRow.external_id ?? null,
        };
    };
    const CustomNoRowsOverlay = ({ isLoading, isSearching, debouncedSearchTerm }) => {
        const showLoading = isLoading || isSearching;
        const [showEmpty, setShowEmpty] = useState(false);
useEffect(() => {
  if (!showLoading && rows.length === 0) {
    const delay = debouncedSearchTerm ? 500 : 2000;
    const timer = setTimeout(() => setShowEmpty(true), delay);
    return () => clearTimeout(timer);
  } else {
    setShowEmpty(false);
  }
}, [showLoading, rows.length, debouncedSearchTerm]);

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    color: "#FFF",
                    fontFamily: "DM Sans",
                    fontSize: "16px",
                    fontWeight: 500,
                }}
            >
           {showLoading ? (
  <CircularProgress
    size={48}
    thickness={3}
    sx={{ color: "rgba(255,255,255,0.3)" }}
  />
) : (
  <Typography
    sx={{
      opacity: showEmpty ? 1 : 0,
      transition: "opacity 0.3s ease",
      textAlign: "center",
      fontFamily: '"DM Sans", sans-serif',
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "16px",
      color: "rgba(255,255,255,0.8)",
    }}
  >
    {debouncedSearchTerm
      ? `No results found for "${debouncedSearchTerm}"`
      : "No cases available"}
  </Typography>
)}
            </Box>
        );
    };


    const { processPdfs, renderUi } = PdfOrderProcessor({ selectedRowIds, apiRef });
    const getOpacity = () => {
        if (gridRef.current) {
            const computedStyle = window.getComputedStyle(gridRef.current);
            return parseFloat(computedStyle.opacity);
        }
        return 1;
    };

    const handleSinglePdfDownload = useCallback((row, errorHandler = handleError) => {
        if (row?.orderform) {
            handleDownloadFile(row.orderform, errorHandler);
        } else {
            errorHandler("No PDF available for this case.");
        }
    }, []);

    const handleSingleZipDownload = useCallback(async (row, fileTypeFilter, setters) => {
        const { setIsDownloading, setProgress, setStatus, setPopperOpen } = setters;
        setIsDownloading(true);
        setProgress(0);
        try {
            const initials = getPatientInitials(row?.patientName);
            const ref = row?.ref || '';
            const orderNumber = row?.id || '';
            const links = [];
            try {
                const parsed = JSON.parse(row?.stlfiles || '[]');
                if (Array.isArray(parsed)) {
                    const filteredLinks = parsed
                        .filter((file) =>
                            typeof file.fileLink === 'string' &&
                            (fileTypeFilter ? file.fileType === fileTypeFilter : true)
                        )
                        .map((file) => file.fileLink);
                    links.push(...filteredLinks);
                }
            } catch (err) {
                console.error('Failed to parse stlfiles:', err);
            }

            if (links.length === 0) {
                throw new Error('No files available for download.');
            }

            const fileEntries = links.map((fileLink) => ({ fileLink, initials, ref, orderNumber }));

            await downloadAndZipFiles(fileEntries, setIsDownloading, setProgress, (message) => {
                setStatus(message);
                setPopperOpen(true);
            });
        } catch (error) {
            console.error('Download error:', error);
            setStatus(`Download failed: ${error.message}`);
            setPopperOpen(true);
        } finally {
            setIsDownloading(false);
            setProgress(0);
        }
    }, []);



    const isSearchActive = !isDashboardVisible && debouncedSearchTerm !== "" && !isSearching;

    const [shouldHide, setShouldHide] = useState(false);
    useEffect(() => { shouldHideRef.current = shouldHide; }, [shouldHide]);
    useEffect(() => {
        if (isDashboardVisible) {
            // Wait for transition to complete before hiding
            const timer = setTimeout(() => {
                setShouldHide(true);
            }, 300); // adjust to match your transition duration
            return () => clearTimeout(timer);
        } else {
            // Immediately show when transitioning back
            setShouldHide(false);
        }
    }, [isDashboardVisible]);
    const showAcceptButton = FEATURE_ACCEPT_ENABLED() && !selectedRowIds.every(id => allRows.find(r => r.id === id)?.status === 'accepted');
    const fabButtonsRef = useRef(null);
    const fabTextRef = useRef(null);
    // Selectie-toolbar: past hij niet in het scherm, dan worden alleen de knoppen
    // (hoogte, padding, tekst, icoon) kleiner; checkbox en "x rows selected"
    // blijven gelijk. Op een breed scherm blijft --fab-s 1 = de oude maten.
    const fabHasSelection = selectedRowIds.length > 0;
    useLayoutEffect(() => {
        if (!fabHasSelection) return;
        const fit = () => {
            const box = fabContainerRef.current, buttons = fabButtonsRef.current, text = fabTextRef.current;
            if (!box || !buttons || !text) return;
            // Zonder transition meten, anders lees je de maten halverwege de animatie
            box.classList.add("fab-measuring");
            box.style.setProperty("--fab-s", "1");
            const natural = buttons.offsetWidth;
            // checkbox 24 + 2 gaps van 12 + padding 2x12 + border 2x1
            const fixed = 24 + 24 + 24 + 2 + text.offsetWidth;
            const avail = window.innerWidth - 32;
            const s = Math.max(0.5, Math.min(1, (avail - fixed) / natural));
            box.style.setProperty("--fab-s", String(s));
            void box.offsetWidth;
            box.classList.remove("fab-measuring");
        };
        fit();
        // Het font kan later binnenkomen; dan klopt de meting pas daarna
        document.fonts?.ready.then(fit);
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, [fabHasSelection, lastSelectedCount, showAcceptButton]);

    const closeDrawer = () => {
        setSelectedRow(null);
        setShouldHide(false); // ← Reset hide bij close
        // ← NEW: Force prepend if pending rows exist and not scrolled down (to handle drawer-open accumulation)
        if (pendingNewRows.length > 0 && !userScrolledDown) {
            prependNewRowsOnce();
        }
    };
    // Browser-terug met de drawer open = Close (terug naar de inbox). Eigen
    // history-stap bovenop die van de inbox (App.js); sluiten via de knop
    // haalt die stap weer weg.
    const closeDrawerRef = useRef(closeDrawer);
    closeDrawerRef.current = closeDrawer;
    const drawerOpen = !!selectedRow && shouldHide;
    const drawerEntryRef = useRef(false);
    useEffect(() => {
        if (drawerOpen && !drawerEntryRef.current) {
            drawerEntryRef.current = true;
            window.history.pushState({ ...window.history.state, drawer: true }, "");
        } else if (!drawerOpen && drawerEntryRef.current) {
            drawerEntryRef.current = false;
            window.history.back();
        }
    }, [drawerOpen]);
    useEffect(() => {
        const onPop = () => {
            if (!drawerEntryRef.current || window.history.state?.drawer) return;
            drawerEntryRef.current = false;
            closeDrawerRef.current();
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    const role = localStorage.getItem("role");
    return (
        <div
            className={`
    datagrid-scroll-container
    ${!isDashboardVisible ? 'show-datagrid' : 'hide-datagrid'}
    ${isSearchActive ? 'search-theme' : ''}
  `.trim()}
            ref={gridRef}
            style={
                shouldHide
                    ? { visibility: 'hidden' }
                    : {}
            }
        >
            <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
                <DataGridPro
                    apiRef={apiRef}
                    rows={rowsWithHeaders}
                    columns={columns}
                    columnVisibilityModel={columnVisibilityModel}
                    disableVirtualization={false}
                    getRowId={(row) => row.id}
                    rowBuffer={20}
                    onRowClick={(params, event) => {
                        if (params.row.isSectionHeader) return;
                        handleRowClick(params, event);
                    }}
                    onCellClick={(params, event) => {
                        // Klik in het vak rond de (kleine) checkbox = ook aan-/uitvinken
                        if (params.field !== "__check__" || params.row.isSectionHeader) return;
                        if (event.target.tagName === "INPUT") return;
                        // Zelfde event als de checkbox zelf, zodat shift-klik (bereik) ook werkt
                        apiRef.current.publishEvent(
                            "rowSelectionCheckboxChange",
                            { id: params.id, value: !apiRef.current.isRowSelected(params.id) },
                            event
                        );
                    }}
                    getRowHeight={(params) => params.row?.isSectionHeader ? 32 : (isCompact ? 96 : 72)}
                    isRowSelectable={(params) => !params.row?.isSectionHeader}
                    getRowClassName={(params) => {
                        if (params.row.isSectionHeader) return "section-header-row";
                        const classes = [];
                        if (apiRef.current.isRowSelected(params.id)) {
                            classes.push("glow-row");
                        }
                        if (params.row.justAdded) classes.push("fade-in-row");
                        if (params.row.isSkeleton) classes.push("skeleton-row");
                        if (params.id === keyboardActiveId) classes.push("keyboard-active-row");
                        classes.push(`hover-row-${params.id}`);
                        return classes.join(" ");
                    }}
                    hideFooter
                    disableColumnMenu
                    disableRowSelectionOnClick={true}
                    checkboxSelection
                    rowHeight={72}
                    slots={{
                        noRowsOverlay: () => (
                            <CustomNoRowsOverlay
                                isLoading={isLoading}
                                isSearching={isSearching}
                                debouncedSearchTerm={debouncedSearchTerm}
                            />
                        ),
                        loadingOverlay: () => (
                            <CustomNoRowsOverlay
                                isLoading={isLoading}
                                isSearching={isSearching}
                                debouncedSearchTerm={debouncedSearchTerm}
                            />
                        ),
                    }}

sx={{
  "& .MuiDataGrid-virtualScroller": {
    paddingBottom: "0px",
    transition: "mask-image 0.3s ease, -webkit-mask-image 0.3s ease",
    maskImage: atBottom
      ? "none"
      : "linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)",
    WebkitMaskImage: atBottom
      ? "none"
      : "linear-gradient(to bottom, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%)",
    scrollbarWidth: "none",       // Firefox
    msOverflowStyle: "none",      // IE/Edge
  },
  "& ::-webkit-scrollbar": {
    width: 0,
    height: 0,
  },
  "& ::-webkit-scrollbar-corner": {
    background: "transparent",
  },
                        "& .MuiDataGrid-filler": {
                            backgroundColor: "rgba(255, 255, 255, 0.05)", // Match virtual scroller
                        },
                        "& .skeleton-row": {
                            background: "rgba(255, 255, 255, 0.05)",
                            animation: "pulse 1.5s infinite ease-in-out",
                        },
                        "@keyframes pulse": {
                            "0%": { opacity: 0.6 },
                            "50%": { opacity: 0.3 },
                            "100%": { opacity: 0.6 },
                        },
                        "& .MuiDataGrid-row": {
                            cursor: "pointer",
                            // Bij uitvinken pas na de fade weer smal afknippen (zie .glow-row in de css)
                            transition: "opacity 0.3s ease, clip-path 0s linear 0.2s",
                            "&.glow-row, &.keyboard-active-row": { transitionDelay: "0s" },
                            "&:not(.skeleton-row):hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.05) !important",
                                // Ook het stukje links van de rij (zie .MuiDataGrid-virtualScroller in de css)
                                boxShadow: "-11px 0 0 0 rgba(255, 255, 255, 0.05)",
                                transitionDelay: "0s",
                            },
                        },
                        "& .MuiDataGrid-cell": {
                            display: "flex",
                            alignItems: "center",
                        },
                        "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root svg, & .MuiDataGrid-cellCheckbox .MuiCheckbox-root:not(.Mui-checked) svg": {
                            color: "rgba(255,255,255,0.5)",
                        },
                        "& .MuiDataGrid-cell[data-field='flag']": {
                            padding: "0 !important",
                            overflow: "visible",
                        },
                    }}
                />
            </Box>

            {createPortal(
                <Fade in={selectedRowIds.length > 0 && !isDashboardVisible} timeout={300} unmountOnExit>
                    <Box
                        ref={fabContainerRef}
                        sx={{
                            pointerEvents: "auto",
                            position: "fixed",
                            bottom: "80px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "12px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255, 255, 255, 0.10)",
                            background: "rgba(255, 255, 255, 0.04)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            padding: "12px",
                            zIndex: 10,
                            // Niet laten wringen door left 50% (dan krijgt hij maar de halve
                            // schermbreedte); te breed = knoppen kleiner, zie --fab-s
                            width: "max-content",
                        }}
                    >
                        <Checkbox
                            checked={selectedRowIds.length > 0}
                            onChange={(event) => {
                                if (!event.target.checked) {
                                    setSelectedRowIds([]);
                                    if (apiRef.current) {
                                        try {
                                            const selectedIds = Array.from(
                                                apiRef.current.getSelectedRows().keys()
                                            );
                                            selectedIds.forEach((id) => {
                                                apiRef.current.selectRow(id, false, false);
                                            });
                                        } catch (error) {
                                            console.error("Error clearing DataGrid selection:", error);
                                        }
                                    } else {
                                        console.warn("apiRef not available");
                                    }
                                }
                            }}
                            sx={{
                                pointerEvents: "auto",
                                padding: 0,
                                width: "24px !important",
                                height: "24px !important",
                                borderRadius: "4px !important",
                                border: "1px solid rgba(255, 255, 255, 0.10) !important",
                                background: "rgba(255, 255, 255, 0.2) !important",
                                boxShadow: selectedRowIds.length > 0
                                    ? "0 0 12px rgba(255, 255, 255, 0.3), 0 0 32px rgba(255, 255, 255, 0.4), 0 0 48px rgba(255, 255, 255, 0.2) !important"
                                    : "none !important",
                                WebkitBoxShadow: selectedRowIds.length > 0
                                    ? "0 0 12px rgba(255, 255, 255, 0.3), 0 0 32px rgba(255, 255, 255, 0.4), 0 0 48px rgba(255, 255, 255, 0.2) !important"
                                    : "none !important",
                                transform: selectedRowIds.length > 0
                                    ? "scale(1) !important"
                                    : "scale(0.68) !important",
                                WebkitTransform: selectedRowIds.length > 0
                                    ? "scale(1) translateZ(0) !important"
                                    : "scale(0.68) translateZ(0) !important",
                                transition: "transform 0.3s ease, box-shadow 0.3s ease !important",
                                color: "#fff !important",
                                "&.Mui-checked": {
                                    color: "#000 !important",
                                },
                                "& .MuiSvgIcon-root": {
                                    display: "block !important",
                                    width: "16px !important",
                                    height: "16px !important",
                                },
                            }}
                        />
                        <Box ref={fabButtonsRef} className="fab-buttons" sx={{ display: "flex", gap: "calc(16px * var(--fab-s, 1))", pointerEvents: "auto" }}>
                            <ButtonGlass
                                onClick={async () => {
                                    setLoadingButton('pdf');
                                    setIsDownloading(true);
                                    try {
                                        await processPdfs();
                                    } finally {
                                        setIsDownloading(false);
                                        setLoadingButton(null);
                                    }
                                }}
                                loading={loadingButton === 'pdf'}
                                icon={isButtonDisabled('pdf', selectedRowIds.length) ? UnavailableIcon : PdfIcon}
                                disabled={isDownloading || isButtonDisabled('pdf', selectedRowIds.length)}
                            >
                                {isButtonDisabled('pdf', selectedRowIds.length) ? `Print PDF` : 'Print PDF'}
                                {/* {isButtonDisabled('pdf', selectedRowIds.length) ? `Max. ${BUTTON_MAX_LIMITS.pdf}` : 'Print PDF'} */}
                            </ButtonGlass>
                            {showAcceptButton && (
                            <ButtonGlass
                                onClick={async () => {
                                    setLoadingButton('accept');
                                    try {
                                        await Promise.all(
                                            selectedRowIds.map(id => {
                                                const row = allRows.find(r => r.id === id);
                                                return apiClient.patch(`/cases/${id}/status`, { status: 'accepted' })
                                                    .then(() => {
                                                        updateRowById(id, { flag: 1, status: 'accepted' });
                                                        if (row?.configuredPortalId) {
                                                            apiClient.post('/processing_queue', { configured_portal_id: row.configuredPortalId, external_id: row.externalId, action: 'status', value: 'accepted' }).catch(console.error);
                                                        }
                                                    });
                                            })
                                        );
                                    } catch (err) {
                                        console.error('Failed to accept cases:', err);
                                    } finally {
                                        setLoadingButton(null);
                                    }
                                }}
                                iconComponent={<TaskAltIcon sx={{ fontSize: 20, color: '#fff' }} />}
                                loading={loadingButton === 'accept'}
                                disabled={isDownloading}
                            >
                                Accept
                            </ButtonGlass>
                            )}
                            <ButtonGlass
                                onClick={() => {
                                    setLoadingButton('zip');
                                    handleDownloadFiles(
                                        selectedRowIds,
                                        'stl',
                                        apiRef,
                                        setIsDownloading,
                                        setProgress,
                                        setStatus,
                                        setPopperOpen
                                    ).finally(() => setLoadingButton(null));
                                }}
                                icon={isButtonDisabled('zip', selectedRowIds.length) ? UnavailableIcon : cad}
                                loading={loadingButton === 'zip'}
                                disabled={isDownloading || isButtonDisabled('zip', selectedRowIds.length)}
                            >
                                {isButtonDisabled('zip', selectedRowIds.length) ? `Download STL` : 'Download STL'}
                            </ButtonGlass>
                            <ButtonGlass
                                onClick={() => {
                                    setLoadingButton('stl');
                                    handleDownloadFiles(
                                        selectedRowIds,
                                        'ply',
                                        apiRef,
                                        setIsDownloading,
                                        setProgress,
                                        setStatus,
                                        setPopperOpen
                                    ).finally(() => setLoadingButton(null));
                                }}
                                icon={isButtonDisabled('stl', selectedRowIds.length) ? UnavailableIcon : PlyIcon}
                                loading={loadingButton === 'stl'}
                                disabled={isDownloading || isButtonDisabled('stl', selectedRowIds.length)}
                            >
                                {isButtonDisabled('stl', selectedRowIds.length) ? `Download PLY` : 'Download PLY'}
                            </ButtonGlass>
                        </Box>
                        <Box ref={fabTextRef}>
                            <Typography
                                sx={{
                                    color: "#fff",
                                    fontFamily: "DM Sans",
                                    fontSize: "14px",
                                    textAlign: "center",
                                    marginTop: "0px", // Space above the text
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {lastSelectedCount} row{lastSelectedCount === 1 ? '' : 's'} selected
                            </Typography>
                        </Box>
                    </Box>
                </Fade>,
                document.body
            )}
            {createPortal(
                <Popper
                    open={popperOpen}
                    sx={{
                        zIndex: 1300,
                        position: 'fixed',
                        top: '32px',
                        left: '32px',
                        transform: 'none', // Prevent unintended translations
                        margin: 0, // Reset Popper margins
                        padding: 0, // Reset Popper paddings
                    }}
                    transition
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={300}>
                            <Paper
                                sx={{
                                    p: 3, // Increased padding for internal breathing room (from p: 2)
                                    m: 1, // Added margin for external breathing room
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                    color: '#fff',
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                                    minWidth: '200px',
                                    textAlign: 'center',
                                    backdropFilter: 'blur(16px)',
                                    border: '1px solid rgba(255, 255, 255, 0.10)', // Preserved border
                                }}
                            >
                                <Typography variant="body2" sx={{ fontFamily: 'DM Sans' }}>{status}</Typography>
                            </Paper>
                        </Fade>
                    )}
                </Popper>,
                document.body
            )}

            {/* Logo linksonder; op een telefoon zou het onder zoeken/filter vallen */}
            {!isDashboardVisible && !isPhone && createPortal(
                <Box
                    sx={{
                        position: "fixed",
                        bottom: "16px",
                        left: "40px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        zIndex: 5,
                    }}
                >
                    <img
                        src={CadmusLight}
                        alt="Cadmus Logo"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        style={{ width: "32px", height: "28px" }}
                    />
                    <Typography
                        sx={{
                            color: "#FFF",
                            fontFamily: "DM Sans",
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: 500,
                            lineHeight: "28px",
                            display: "flex",
                            userSelect: "none",
                            alignItems: "center",
                        }}
                    >
                        Cadmus
                    </Typography>
                </Box>,
                document.body
            )}

            {!isDashboardVisible &&
                debouncedSearchTerm !== "" &&
                !isSearching && (
                    createPortal(
                        <motion.div
                            key="search-results-banner" // stabiele key helpt soms mee
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{
                                duration: 0.45,           // terug naar normale snelheid
                                ease: "circOut",
                            }}
                            style={{
                                position: "fixed",
                                top: isMobile ? "60px" : "92px",
                                left: isMobile ? "8px" : "calc(50% - 766px)",
                                width: isMobile ? "calc(100% - 16px)" : "1532px",
                                pointerEvents: "none",
                            }}
                        >
                            {/* Wrapper zodat pointerEvents alleen op de tekst werken */}
                            <div style={{ pointerEvents: "auto", userSelect: "none" }}>
                                {/* Back link – klein, 16px */}
                                <Typography
                                    onClick={() => {
                                        // Hier komt jouw actie:
                                        // Optie 1: zoekopdracht resetten (meest logisch)
                                        setSearchTerm("");           // ← pas naam aan naar jouw setter
                                        // setDebouncedSearchTerm(""); // als je die apart hebt

                                        // Optie 2: dashboard weer zichtbaar maken
                                        // setIsDashboardVisible(true);

                                        // Optie 3: beide of iets anders, zoals router.push('/inbox')
                                    }}
                                    sx={{
                                        color: "#fff",                // subtiel grijs
                                        fontFamily: "DM Sans",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                        marginBottom: "4px",
                                        display: "inline-flex",
                                        letterSpacing: "0px",
                                        alignItems: "center",

                                        transition: "color 0.2s",
                                        "&:hover": {
                                            color: "#fff",
                                        },
                                    }}
                                >
                                    ← Back to inbox
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "#FFF",
                                        fontFamily: "DM Sans",
                                        fontSize: "64px",
                                        fontWeight: 500,
                                        lineHeight: "100%",
                                        letterSpacing: "-1.28px",
                                        '& .count': {
                                            color: 'rgba(255, 255, 255, 0.4)',
                                        }
                                    }}
                                >
                                    Search results <span className="count">{rowCountState}</span>
                                </Typography>
                            </div>
                        </motion.div>,
                        document.body
                    )
                )}



            {createPortal(
                <Box
                    sx={{
                        position: "fixed",
                        bottom: "16px",
                        right: "40px", // 40px from right edge
                        display: "flex",
                        alignItems: "center",
                        gap: "40px", // 40px between items
                        zIndex: 5,
                    }}
                >
                    {[
                        // {
                        // label: "Inbox",
                        // icon: inbox,
                        // onClick: () => {
                        // // Add inbox functionality here
                        // console.log("Inbox clicked");
                        // }
                        // },
                        // Telefoon: in de lijst valt dit onder de zoekbalk, dus alleen op het dashboard
                        ...((role === "Admin" || role === "SuperAdmin") && !(isPhone && !isDashboardVisible) ? [{
                            label: "Console",
                            icon: consoleIcon,
                            onClick: () => { window.dispatchEvent(new CustomEvent('scoreboardGlitch')); setTimeout(() => setConsoleOpen(true), 120); }
                        }] : [])
                    ].map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer", // Show pointer cursor
                                "&:hover": {
                                    opacity: 0.8 // Optional: add hover effect
                                }
                            }}
                            onClick={() => setTimeout(item.onClick, 50)}
                        >
                            <img
                                src={item.icon}
                                alt={`${item.label} icon`}
                                style={{ width: "20px", height: "20px" }}
                            />
                            <Typography
                                sx={{
                                    color: "#FFF",
                                    fontFamily: "DM Sans",
                                    fontSize: "14px",
                                    fontStyle: "normal",
                                    fontWeight: 500,
                                    lineHeight: "84%",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>,
                document.body
            )}
            <ConsoleDialog
                open={consoleOpen}
                onClose={() => setConsoleOpen(false)}
                title="Admin Console"
            />
            {createPortal(
                <Snackbar
                    open={showNewRowsNotification && !isDashboardVisible && pendingNewRows.length > 0} // ← Extra guard against 0
                    onClick={() => handleScrollToTop()}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    message={
                        <>
                            <BadgeBorderSweep key={`pill-${badgeSweepKey}`} />
                            {/* Rondje + logo als één SVG: een border-radius met halfdoorzichtige
                                border gaf een rafelige rand. */}
                            <svg
                                aria-hidden
                                width="34"
                                height="34"
                                viewBox="0 0 34 34"
                                style={{ position: "absolute", top: "1px", left: "1px", display: "block" }}
                            >
                                <circle cx="17" cy="17" r="17" fill="#6C48C8" />
                                <g transform="translate(5 5) scale(1.5)">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M14.6103 3.49272C14.6465 3.54559 14.6294 3.61779 14.574 3.64981L9.7177 6.45359C9.68333 6.47343 9.64097 6.47343 9.60659 6.45359L8.88359 6.03616C7.28928 5.11569 5.25065 5.66194 4.33017 7.25624C3.89023 8.01824 3.78534 8.88174 3.9698 9.67615C3.98109 9.7248 3.96001 9.77551 3.91676 9.80048L2.13478 10.8293C2.03633 10.8861 2.07666 11.0366 2.19034 11.0366L4.58569 11.0366C4.61898 11.0366 4.65044 11.0516 4.67195 11.077C4.91357 11.3625 5.20773 11.6119 5.55025 11.8097L8.20109 13.3401C9.69711 14.2039 11.3971 14.3943 12.9502 14.0056C13.0663 13.9765 13.1492 14.124 13.0565 14.1997C11.6783 15.325 9.91799 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C10.7465 0 13.1698 1.38404 14.6103 3.49272Z" fill="white" />
                                    <path opacity="0.4" fillRule="evenodd" clipRule="evenodd" d="M12.5744 1.43727C13.4028 2.01596 14.1166 2.74749 14.6747 3.59096L9.6612 6.48553L9.66016 6.48493L12.5744 1.43727Z" fill="url(#newOrdersBadgeLogoGrad)" />
                                </g>
                                <defs>
                                    <linearGradient id="newOrdersBadgeLogoGrad" x1="9.66433" y1="2.22227" x2="9.66433" y2="8.44492" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#111111" />
                                        <stop offset="1" stopColor="#111111" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <BadgeBorderSweep
                                key={`circle-${badgeSweepKey}`}
                                shape="circle"
                                sx={{ inset: "auto", top: "1px", left: "1px", width: "34px", height: "34px" }}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start !important",
                                    height: "36px",
                                    paddingLeft: "42px",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#FFF",
                                        fontFamily: "DM Sans",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        lineHeight: "20px",
                                        margin: 0,
                                        padding: 0,
                                    }}
                                >
                                    {`${pendingNewRows.length} new ${pendingNewRows.length === 1 ? "order" : "orders"
                                        } received`}
                                </Typography>
                            </Box>
                        </>
                    }
                    onClose={(event, reason) => {
                        if (reason !== "clickaway") setShowNewRowsNotification(false);
                    }}
                    sx={{
                        zIndex: 1,
                        // Centreren via flex i.p.v. MUI's translateX(-50%): bij een oneven
                        // breedte landt die op een halve pixel en wordt logo + tekst wazig.
                        left: "0 !important",
                        right: "0 !important",
                        transform: "none !important",
                        justifyContent: "center",
                        pointerEvents: "none",
                        "& .MuiSnackbarContent-root": {
                            pointerEvents: "auto",
                            top: "34px",
                            position: "relative",
                            padding: "0 16px 0 0",
                            height: "36px",
                            borderRadius: "48px",
                            background: "rgba(255, 255, 255, 0.10)",
                            boxShadow:
                                "0px 0px 16px 0px rgba(255, 255, 255, 0.20) inset, 0px 1px 1px 0px rgba(17, 17, 17, 0.20), 0px 32px 64px 0px rgba(17, 17, 17, 0.40)",
                            backdropFilter: "blur(12px)",
                            cursor: "pointer",
                            minWidth: 0,
                            "&:hover": {
                                background: "rgba(255, 255, 255, 0.20)",
                            },
                        },
                    }}
                />,
                document.body
            )}
            {!isDashboardVisible &&
                createPortal(
                    <Slide in={showScrollToTop} direction="up" timeout={300}>
                        <Fab
                            ref={fabRef}
                            onClick={() => handleScrollToTop()}
                            sx={{
                                position: "fixed",
                                bottom: "60px",
                                left: `${fabLeft}px`,
                                borderRadius: "100px",
                                border: "1px solid rgba(255, 255, 255, 0.10)",
                                background: "rgba(255, 255, 255, 0.10)",
                                backdropFilter: "blur(4px)",
                                width: "48px",
                                height: "48px",
                                padding: "14px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexShrink: 0,
                                zIndex: 2,
                                "&:hover": {
                                    background: "rgba(255, 255, 255, 0.20)",
                                },
                            }}
                        >
                            <ArrowUpwardIcon sx={{ color: "#FFF" }} />
                        </Fab>
                    </Slide>,
                    document.body
                )}
            {!isDashboardVisible &&
                createPortal(
                    <Box
                        sx={{
                            position: "fixed",
                            bottom: atBottom ? "56px" : "16px",
                            // Telefoon: 16px lucht aan beide kanten i.p.v. vaste 432px
                            ...(isPhone
                                ? { left: 16, right: 16, bottom: `calc(env(safe-area-inset-bottom, 0px) + ${atBottom ? 56 : 16}px)` }
                                : { left: `calc(50% - 216px)`, width: 432 }),
                            zIndex: 15,
                            pointerEvents: "auto",
                            opacity: shouldHide ? 0 : 1,
                            transition: "bottom 0.4s ease, opacity 0.4s ease",
                            visibility: shouldHide ? "hidden" : "visible",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                        }}
                    >
                        {/* Left side: morphs between filter circle and filter pill */}
                        {FEATURE_FILTER_MODE_ENABLED() && <motion.div
                            layout
                            transition={{ type: "spring", stiffness: 500, damping: 35, mass: 0.6 }}
                            style={{ flexShrink: 0, overflow: "hidden" }}
                        >
                            <AnimatePresence mode="popLayout" initial={false}>
                                {activeFilters.length > 0 ? (
                                    <motion.div
                                        key="pill"
                                        initial={{ opacity: 0, scale: 0.92 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.92 }}
                                        transition={{ type: "spring", stiffness: 500, damping: 35, mass: 0.6 }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex", alignItems: "center",
                                                height: "40px",
                                                background: "rgba(30,30,32,0.85)",
                                                backdropFilter: "blur(16px)",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                borderRadius: 999,
                                                padding: "0 12px 0 4px",
                                                gap: "8px",
                                                userSelect: "none", whiteSpace: "nowrap",
                                            }}
                                        >
                                            {/* Circle — click to clear filter */}
                                            <Box
                                                onClick={() => startTransition(() => setActiveFilters([]))}
                                                sx={{
                                                    width: 32, height: 32, borderRadius: "50%",
                                                    background: "#ffffff", flexShrink: 0,
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    cursor: "pointer",
                                                    "&:hover": { background: "#e8e8e8" },
                                                }}
                                            >
                                                <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
                                                    <line x1="0" y1="1" x2="15" y2="1" stroke="black" strokeWidth="2" strokeLinecap="round"/>
                                                    <line x1="2.5" y1="5.5" x2="12.5" y2="5.5" stroke="black" strokeWidth="2" strokeLinecap="round"/>
                                                    <line x1="5" y1="10" x2="10" y2="10" stroke="black" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                            </Box>
                                            <Box sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 500, fontFamily: '"DM Sans", sans-serif' }}>Filtered:</Box>
                                            <Box sx={{ color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: '"DM Sans", sans-serif' }}>
                                                {activeFilters.join(', ')}
                                            </Box>
                                        </Box>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="circle"
                                        initial={{ opacity: 0, scale: 0.92 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.92 }}
                                        transition={{ type: "spring", stiffness: 500, damping: 35, mass: 0.6 }}
                                    >
                                        <IconButton
                                            ref={filterButtonRef}
                                            onClick={() => startTransition(() => setActiveFilters(["Not Accepted"]))}
                                            sx={{
                                                width: 40, height: 40, borderRadius: "50%",
                                                background: "rgba(255,255,255,0.08)",
                                                backdropFilter: "blur(12px)",
                                                border: "1px solid rgba(255,255,255,0.18)",
                                                color: "#fff",
                                                "&:hover": { background: "rgba(255,255,255,0.15)" },
                                            }}
                                        >
                                            <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
                                                <line x1="0" y1="1" x2="15" y2="1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                                <line x1="2.5" y1="5.5" x2="12.5" y2="5.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                                <line x1="5" y1="10" x2="10" y2="10" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                        </IconButton>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>}

                        {/* Right side: morphs between search bar and search circle */}
                        <motion.div
                            layout
                            transition={{ type: "spring", stiffness: 500, damping: 35, mass: 0.6 }}
                            style={{ flex: activeFilters.length > 0 ? "0 0 auto" : "1 1 auto", overflow: "hidden", minWidth: 0 }}
                        >
                            <AnimatePresence mode="popLayout" initial={false}>
                                {activeFilters.length > 0 ? (
                                    <motion.div
                                        key="search-circle"
                                        initial={{ opacity: 0, scale: 0.92 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.92 }}
                                        transition={{ type: "spring", stiffness: 500, damping: 35, mass: 0.6 }}
                                    >
                                        <IconButton
                                            onClick={() => {
                                                startTransition(() => setActiveFilters([]));
                                                setTimeout(() => searchInputRef.current?.focus(), 80);
                                            }}
                                            sx={{
                                                width: 40, height: 40, borderRadius: "50%",
                                                background: "rgba(255,255,255,0.08)",
                                                backdropFilter: "blur(12px)",
                                                border: "1px solid rgba(255,255,255,0.18)",
                                                color: "#fff",
                                                "&:hover": { background: "rgba(255,255,255,0.15)" },
                                            }}
                                        >
                                            <SearchIcon sx={{ color: '#fff', fontSize: 20 }} />
                                        </IconButton>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="search-bar"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.12 }}
                                        style={{ width: "100%" }}
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Search by patient name ..."
                                            value={searchTerm}
                                            inputRef={searchInputRef}
                                            onFocus={() => setSearchFocused(true)}
                                            onBlur={() => setSearchFocused(false)}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                if (newValue !== searchTerm) setSearchTerm(newValue);
                                            }}
                                            autoComplete="off"
                                            spellCheck="false"
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            {isSearching ? (
                                                                <Box sx={{
                                                                    width: 16, height: 16,
                                                                    border: '2px solid rgba(255,255,255,0.3)',
                                                                    borderTopColor: '#fff', borderRadius: '50%',
                                                                    animation: 'spin 0.8s linear infinite',
                                                                    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } }
                                                                }} />
                                                            ) : (
                                                                <SearchIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: searchTerm && (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setSearchTerm('')}
                                                                sx={{
                                                                    color: "rgba(255,255,255,0.6)", padding: '4px',
                                                                    '&:hover': { color: "rgba(255,255,255,0.9)", backgroundColor: "rgba(255,255,255,0.1)" }
                                                                }}
                                                            >
                                                                <Box sx={{
                                                                    width: 16, height: 16, borderRadius: '50%',
                                                                    border: '1.5px solid currentColor',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                                                                    '&::before, &::after': { content: '""', position: 'absolute', width: '8px', height: '1.5px', backgroundColor: 'currentColor' },
                                                                    '&::before': { transform: 'rotate(45deg)' },
                                                                    '&::after': { transform: 'rotate(-45deg)' }
                                                                }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                            sx={{
                                                "& .MuiInputBase-root": {
                                                    height: "40px",
                                                    borderRadius: "20px",
                                                    background: "rgba(255,255,255,0.08)",
                                                    backdropFilter: "blur(12px)",
                                                    border: "1px solid rgba(255,255,255,0.18)",
                                                    color: "#fff", fontSize: "14px", fontFamily: '"DM Sans", sans-serif',
                                                    "& fieldset": { borderColor: "transparent" },
                                                    "&:hover fieldset": { borderColor: "transparent" },
                                                    "&.Mui-focused fieldset": { borderColor: "transparent" },
                                                },
                                                "& .MuiInputBase-input": {
                                                    padding: searchFocused || searchTerm ? "8px 14px 8px 8px" : "8px 46px 8px 8px",
                                                    textAlign: searchFocused || searchTerm ? "left" : "center",
                                                    transition: "padding 0.3s ease",
                                                    "&::placeholder": {
                                                        textAlign: "center",
                                                        opacity: 0.6,
                                                        color: "#FFF",
                                                        fontFamily: '"DM Sans"',
                                                        fontSize: "14px",
                                                        fontStyle: "normal",
                                                        fontWeight: 400,
                                                        lineHeight: "16px",
                                                        transition: "opacity 0.3s ease",
                                                    },
                                                    "&:focus::placeholder": { opacity: 0 },
                                                },
                                            }}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </Box>,
                    document.body
                )}

            <CaseDetailDrawer
                open={drawerOpen}
                rowData={selectedRow}
                onClose={closeDrawer}
                onOlderRow={handleOlderRow}
                onNewerRow={handleNewerRow}
                neighborRows={neighborRows}
                index={selectedRow?.index ?? 0}
                allRowsLength={allRows.length}
                pendingNewRows={pendingNewRows}
                isLoading={isLoading}
                // NIEUW: Download callbacks doorgeven
                onDownloadPdf={(row) => handleSinglePdfDownload(row)}
                onDownloadZipWithPdf={(row) => handleSingleZipDownload(row, 'stl', { setIsDownloading, setProgress, setStatus, setPopperOpen })}
                onDownloadStlZip={(row) => handleSingleZipDownload(row, 'ply', { setIsDownloading, setProgress, setStatus, setPopperOpen })}
                onUpdateRow={(id, updates) => {
                    updateRowById(id, updates);
                }}
                // Optioneel: Doorloop states voor loading indicators in drawer
                isDownloading={isDownloading}
                progress={progress}
            />
        </div>
    );
};
export default TransparentDataGrid;